import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { ServerTokenService } from '../../services/server-token.service';
import { CustomAuthServiceService } from '../../auth/custom-auth-service.service';
import { isAdmin, isReportsAdmin, ROLE_NUMBER_CLINICAL, ROLE_NUMBER_CLINICAL_CI } from 'list-types-public';
import {
  DISCHARGE_PLAN_REPORT_ID,
  DISCHARGE_PLAN_REPORT_TYPE_WIRE,
  HIGH_RISK_REPORT_TYPE_WIRE,
  MANAGER_DASHBOARD_REPORT_TYPE_WIRE,
  REPORT_TYPE_BANDS,
  REPORT_TYPE_LABELS,
  ScheduleOverviewRow,
} from '../report-notifications.types';
import { formatFrequencyLabel } from '../shared/scheduled-notifications/schedule-label';
import { DrawerOptionSection } from '../shared/high-risk-content-drawer/drawer-options';
import { DischargePlanService } from '../../manageb/discharge-plan/discharge-plan.service';

const SCHEDULE_ZONE = 'America/New_York';

/** Mirrors the server's requireReportScheduler (middleware/requireReportScheduler.ts),
 *  which gates the list this page is built on. Kept in sync by hand. */
const ROLE_NUMBER_GLOBAL_ADMIN = 190;
export function canSeeSchedules(role: number): boolean {
  return isReportsAdmin(role)
    || role === ROLE_NUMBER_GLOBAL_ADMIN
    || role === ROLE_NUMBER_CLINICAL
    || role === ROLE_NUMBER_CLINICAL_CI;
}

/** A report an admin can schedule from HERE while it has no schedule yet.
 *  Every other type is created from its own report screen; these singletons
 *  have a drawer of their own, so the list can offer them directly. */
interface SetupEntry {
  reportType: string;
  reportId: string;
  label: string;
}

const SETUP_REPORTS: ReadonlyArray<SetupEntry> = [
  {
    reportType: DISCHARGE_PLAN_REPORT_TYPE_WIRE,
    reportId: DISCHARGE_PLAN_REPORT_ID,
    label: REPORT_TYPE_LABELS[DISCHARGE_PLAN_REPORT_TYPE_WIRE],
  },
];

/** The two report types the server manages for administrators only. */
const ADMIN_MANAGED_TYPES: ReadonlyArray<string> = [HIGH_RISK_REPORT_TYPE_WIRE, DISCHARGE_PLAN_REPORT_TYPE_WIRE];

/**
 * Region-wide inventory of every scheduled email: what goes out, when, and to
 * whom. Each row links to the editor for that report, and can be paused here
 * without opening it.
 */
@Component({
  selector: 'app-scheduled-emails',
  templateUrl: './scheduled-emails.component.html',
  styleUrls: ['./scheduled-emails.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduledEmailsComponent implements OnInit, OnDestroy {
  rows: ScheduleOverviewRow[] = [];
  loading = true;
  loadError = '';
  actionError = '';
  expandedKey: string | null = null;
  busyKey: string | null = null;
  confirmingKey: string | null = null;
  /** Delete asks for the report's name to be typed back; this is what was typed. */
  deleteTyped = '';
  /** Test asks once before it sends - it is a real email, to a real inbox. */
  confirmingTestKey: string | null = null;
  testingKey: string | null = null;
  testResult: { key: string; ok: boolean; message: string } | null = null;

  /** The row whose editor drawer is open, or null. */
  editing: ScheduleOverviewRow | null = null;

  /** isAdmin (not the nav-role category) - it is the server's own predicate,
   *  so 152 keeps the access the API actually grants. */
  readonly isAdminUser: boolean;
  /** Who may open this page at all: the same roles the server lets list the
   *  schedules. Anyone else gets a plain "no access" panel, never a 403. */
  readonly canOpen: boolean;
  /** Mirrors the server's requireScheduledEmailAdmin: isAdmin plus the
   *  Global Portal Admin (190), who has full access everywhere else. */
  readonly canAdminister: boolean;
  /** Where a test email lands: the signed-in account, always. '' when the
   *  session does not carry one - the sentence branches rather than printing
   *  a placeholder where an address belongs. */
  readonly myEmail: string;

  private destroy$ = new Subject<void>();

  constructor(
    private server: ServerTokenService,
    private auth: CustomAuthServiceService,
    private dischargePlans: DischargePlanService,
    private cdr: ChangeDetectorRef,
  ) {
    // Once, not per row per change-detection pass: the session is
    // localStorage-backed and this table is region-wide.
    const session = this.auth.currentUserValue();
    const role = Number(session?.role);
    this.isAdminUser = isAdmin(role);
    this.canOpen = canSeeSchedules(role);
    this.canAdminister = this.isAdminUser || role === ROLE_NUMBER_GLOBAL_ADMIN;
    this.myEmail = String(session?.user?.email || '');
  }

  /** Changing anything FROM THIS PAGE - schedule, on/off, delete, test - is
   *  for administrators (owner's decision 2026-09-08). Report managers still
   *  manage their own report's schedule from that report's screen; here they
   *  read. High Risk and Discharge Plan are admin-only END TO END on the
   *  server (requireHighRiskAdmin / requireDischargePlanAdmin are isAdmin,
   *  not isAdmin-or-190), so 190 must not be offered controls on those rows
   *  that can only ever 403.
   */
  canManageRow(row: ScheduleOverviewRow): boolean {
    if (this.isAdminManaged(row)) return this.isAdminUser;
    return this.canAdminister;
  }

  /** True while a row's kind is admin-only on the server too, for the tag. */
  isAdminManaged(row: ScheduleOverviewRow): boolean {
    return ADMIN_MANAGED_TYPES.includes(row.reportType);
  }

  /** Singleton reports with no schedule yet that an admin can set up from
   *  here - the Discharge Plan's own screen offers the same drawer. */
  get unscheduled(): SetupEntry[] {
    if (!this.isAdminUser) return [];
    return SETUP_REPORTS.filter(entry =>
      !this.rows.some(row => row.reportType === entry.reportType && row.reportId === entry.reportId));
  }

  /** Opens the editor on a report that has no schedule; the drawer creates
   *  one on Save (createOnSave), and onEditorClosed reloads the list. */
  setUp(entry: SetupEntry): void {
    this.editing = {
      reportType: entry.reportType,
      reportId: entry.reportId,
      reportName: entry.label,
      // There is no schedule row yet - that is the whole point of this entry.
      exists: false,
      frequency: 'daily',
      weeklyDays: [],
      monthlyDays: [],
      timeEST: '07:00',
      enabled: false,
      nextFireAt: null,
      lastExecutedAt: null,
      lastRun: null,
      recipients: [],
      updatedAt: null,
      updatedBy: null,
    };
    this.cdr.markForCheck();
  }

  /** The Discharge Plan email carries content options (facilities, window,
   *  notes, layout) that live in its own settings; the drawer renders them as
   *  cards when it is handed the loader/saver. Arrow properties so `this`
   *  survives the input hand-off. */
  loadDischargeOptions = this.dischargePlans.loadDrawerOptions;
  saveDischargeOptions = this.dischargePlans.saveDrawerOptions;

  get editingOptionsLoader(): (() => Observable<DrawerOptionSection[]>) | undefined {
    return this.editing?.reportType === DISCHARGE_PLAN_REPORT_TYPE_WIRE ? this.loadDischargeOptions : undefined;
  }

  get editingOptionsSaver(): ((sections: DrawerOptionSection[]) => Observable<unknown>) | undefined {
    return this.editing?.reportType === DISCHARGE_PLAN_REPORT_TYPE_WIRE ? this.saveDischargeOptions : undefined;
  }

  /** The server says why on a refusal (`{ msg }` from the shared guards); a
   *  403 is not a transient failure, so it must not read as "try again". */
  private refusalOr(err: unknown, fallback: string): string {
    const refusal = err as { status?: number; error?: { msg?: string } } | null;
    const msg = typeof refusal?.error?.msg === 'string' ? refusal.error.msg : '';
    return refusal?.status === 403 && msg ? msg : fallback;
  }

  ngOnInit(): void {
    if (!this.canOpen) {
      this.loading = false;
      return;
    }
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get activeCount(): number {
    return this.rows.filter(r => r.enabled).length;
  }

  get recipientlessCount(): number {
    return this.rows.filter(r => r.enabled && !r.recipients.length).length;
  }

  keyOf(row: ScheduleOverviewRow): string {
    return `${row.reportType}::${row.reportId}`;
  }

  /**
   * The pastel band this report wears, 1..6. Keyed by report TYPE so the colour
   * survives sorting and filtering; an unknown type gets a stable band from its
   * own characters rather than falling into a shared "other" bucket.
   */
  bandOf(row: ScheduleOverviewRow): number {
    const known = REPORT_TYPE_BANDS[row.reportType];
    if (known) return known;
    let sum = 0;
    for (let i = 0; i < row.reportType.length; i++) sum += row.reportType.charCodeAt(i);
    return (sum % 6) + 1;
  }

  typeLabel(row: ScheduleOverviewRow): string {
    return REPORT_TYPE_LABELS[row.reportType] ?? row.reportType.replace(/_/g, ' ');
  }

  nameLabel(row: ScheduleOverviewRow): string {
    if (row.reportName) return row.reportName;
    return this.typeLabel(row);
  }

  scheduleLabel(row: ScheduleOverviewRow): string {
    return formatFrequencyLabel(row);
  }

  /** "Sun, Aug 17 at 7:00 AM EST" plus a relative hint, in the schedule's own zone. */
  nextSendLabel(row: ScheduleOverviewRow): string {
    if (!row.enabled) return 'Paused';
    if (!row.nextFireAt) return 'Not scheduled';
    const dt = DateTime.fromISO(row.nextFireAt).setZone(SCHEDULE_ZONE);
    if (!dt.isValid) return 'Not scheduled';
    const relative = dt.toRelative() ?? '';
    return `${dt.toFormat('ccc, LLL d')} at ${dt.toFormat('h:mm a')} EST${relative ? ` (${relative})` : ''}`;
  }

  lastRunLabel(row: ScheduleOverviewRow): string {
    const iso = row.lastRun?.executedAt ?? row.lastRun?.startedAt ?? row.lastExecutedAt;
    if (!iso) return 'Never sent';
    const dt = DateTime.fromISO(iso).setZone(SCHEDULE_ZONE);
    if (!dt.isValid) return 'Never sent';
    return `${dt.toFormat('LLL d')} at ${dt.toFormat('h:mm a')} EST`;
  }

  /** Short outcome of the last run: "3 sent", "1 failed", "no data". */
  lastRunOutcome(row: ScheduleOverviewRow): string {
    const run = row.lastRun;
    // The scheduler stamps lastExecutedAt even for a run that bailed out before
    // it could record anything (no recipients, unknown report, a crash). Without
    // this the date alone reads as "it was delivered", which is the opposite of
    // what happened.
    if (!run) return row.lastExecutedAt ? 'no send recorded' : '';
    // The executor's own words when it ended a run without sending, e.g.
    // "No recipients subscribed". Far more useful than a generic label.
    if (run.outcomeReason && !run.sentCount) return run.outcomeReason;
    if (run.phase === 'failure') return 'run failed';
    if (run.phase === 'skipped') return 'skipped';
    const parts: string[] = [];
    if (run.sentCount) parts.push(`${run.sentCount} sent`);
    if (run.errorCount) parts.push(`${run.errorCount} failed`);
    if (run.skippedCount) parts.push(`${run.skippedCount} skipped`);
    if (parts.length) return parts.join(', ');
    return run.phase === 'done' ? 'nothing to send' : `${run.phase}…`;
  }

  outcomeClass(row: ScheduleOverviewRow): string {
    const run = row.lastRun;
    if (!run) return row.lastExecutedAt ? 'sce__pill--warn' : '';
    if (run.phase === 'failure' || run.status === 'failure' || run.errorCount) return 'sce__pill--bad';
    if (run.status === 'partial_failure' || run.phase === 'skipped') return 'sce__pill--warn';
    if (run.sentCount) return 'sce__pill--good';
    return '';
  }

  /**
   * High Risk has its own richer page (contents + facility scope on top of the
   * schedule); everything else uses the generic schedule editor.
   */
  /** Editing happens IN PLACE - a drawer over the table, so the inventory
   *  never loses its scroll position or its context. The standalone editor
   *  page still exists for a hand-typed URL. */
  editRow(row: ScheduleOverviewRow): void {
    // Closing the editor reloads the list, and a reload during another row's
    // write would hand that write a row object it can no longer find.
    if (this.busyKey) return;
    this.editing = row;
    this.cdr.markForCheck();
  }

  onEditorClosed(): void {
    this.editing = null;
    // The drawer may have changed a schedule or its recipients; refresh so the
    // row's next-send and recipient count are not stale behind it.
    this.load();
    this.cdr.markForCheck();
  }

  /** A save that lands while the editor is still up is followed by the
   *  drawer closing itself, and onEditorClosed reloads then. Only a save that
   *  lands AFTER the admin dismissed the drawer has nobody else to refresh the
   *  row it changed: the reload that ran on close happened BEFORE the write
   *  landed, so this second one is the one that shows what persisted. */
  onEditorSaved(): void {
    // load(), not reload(): a write landing at an arbitrary later moment must
    // not wipe a delete confirmation the admin is typing into right now.
    if (!this.editing) this.load();
  }

  /** The drawer's write failed after the admin had already dismissed it -
   *  nobody is looking at the drawer, so the page reports it. */
  onEditorSaveFailed(message: string): void {
    this.actionError = message;
    this.cdr.markForCheck();
  }

  toggleExpand(row: ScheduleOverviewRow): void {
    const key = this.keyOf(row);
    this.expandedKey = this.expandedKey === key ? null : key;
    this.cdr.markForCheck();
  }

  setEnabled(row: ScheduleOverviewRow, enabled: boolean): void {
    const key = this.keyOf(row);
    if (this.busyKey) return;
    this.busyKey = key;
    this.actionError = '';
    this.cdr.markForCheck();

    this.server.patchWithToken<{ schedule: { enabled: boolean; nextFireAt?: string } }>(
      `report-notifications/reports/${encodeURIComponent(row.reportId)}/enabled?reportType=${encodeURIComponent(row.reportType)}`,
      { enabled },
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          // Take the server's row back rather than assuming: resuming re-anchors
          // nextFireAt, and the list must show the real next send.
          // Matched by key, not identity: a Refresh that landed meanwhile has
          // replaced every row object, and the flip must still reach the row.
          this.rows = this.rows.map(r => this.keyOf(r) === key
            ? { ...r, enabled: res?.schedule?.enabled ?? enabled, nextFireAt: res?.schedule?.nextFireAt ?? r.nextFireAt }
            : r);
          this.busyKey = null;
          this.cdr.markForCheck();
        },
        error: err => {
          this.actionError = this.refusalOr(err,
            `Could not ${enabled ? 'resume' : 'pause'} "${this.nameLabel(row)}". Please try again.`);
          this.busyKey = null;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Build and email this report to the signed-in admin only. Works regardless of
   * whether the background dispatcher is enabled on this server, which is the
   * point: it is how you verify a report without waiting for its slot.
   */
  askTest(row: ScheduleOverviewRow): void {
    if (this.busyKey) return;
    this.confirmingTestKey = this.keyOf(row);
    this.confirmingKey = null;
    this.testResult = null;
    this.actionError = '';
    this.cdr.markForCheck();
  }

  cancelTest(): void {
    this.confirmingTestKey = null;
    this.cdr.markForCheck();
  }

  sendTest(row: ScheduleOverviewRow): void {
    const key = this.keyOf(row);
    if (this.busyKey) return;
    this.confirmingTestKey = null;
    this.busyKey = key;
    this.testingKey = key;
    this.actionError = '';
    this.testResult = null;
    this.cdr.markForCheck();

    this.server.postWithToken<{ sent: boolean; reason?: string; attachmentBytes?: number; facilityCount?: number }>(
      `report-notifications/reports/${encodeURIComponent(row.reportId)}/send-test?reportType=${encodeURIComponent(row.reportType)}`,
      {},
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.testResult = res?.sent
            ? { key, ok: true, message: `Sent to you now (${this.describeTestSend(res)}). Check your inbox.` }
            : { key, ok: false, message: res?.reason || 'Nothing was sent.' };
          this.busyKey = null;
          this.testingKey = null;
          this.cdr.markForCheck();
        },
        error: err => {
          this.testResult = { key, ok: false, message: this.describeTestFailure(err) };
          this.busyKey = null;
          this.testingKey = null;
          this.cdr.markForCheck();
        },
      });
  }

  /** No attachment is a real outcome, not a broken file: the generators
   *  return nothing to attach when the window holds no rows. */
  private describeTestSend(res: { attachmentBytes?: number; facilityCount?: number }): string {
    const facilities = `${res?.facilityCount ?? 0} facilities`;
    const bytes = res?.attachmentBytes ?? 0;
    if (!bytes) return `${facilities}, no attachment - nothing to report in this window`;
    return `${facilities}, ${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  /**
   * Turn a transport failure into something actionable. A 404 here is not "the
   * report is missing" - the route itself is absent, which on this stack means
   * the API server is still running a build from before this feature. Saying so
   * beats a shrug: the server runs a compiled dist, so new endpoints are 404
   * until it is restarted.
   */
  private describeTestFailure(err: unknown): string {
    const failure = err as { status?: number; error?: { reason?: string; msg?: string } } | null;
    const serverReason = failure?.error?.reason || failure?.error?.msg;
    if (serverReason) return serverReason;
    switch (failure?.status) {
      case 404:
        return 'This API server does not have the Test endpoint yet - restart the server so it picks up the new build.';
      case 403:
        return 'Your role is not allowed to send report emails.';
      case 401:
        return 'Your session expired. Sign in again and retry.';
      case 0:
        return 'Could not reach the API server. Is it running?';
      default:
        return `The server could not send the test email${failure?.status ? ` (HTTP ${failure.status})` : ''}.`;
    }
  }

  askDelete(row: ScheduleOverviewRow): void {
    if (this.busyKey) return;
    this.confirmingKey = this.keyOf(row);
    this.confirmingTestKey = null;
    this.deleteTyped = '';
    this.actionError = '';
    this.cdr.markForCheck();
  }

  onDeleteTyped(value: string): void {
    this.deleteTyped = value;
    this.cdr.markForCheck();
  }

  /** The typed name must match the row's name, ignoring case and spacing -
   *  the point is a deliberate act, not a spelling test. */
  deleteConfirmed(row: ScheduleOverviewRow): boolean {
    const norm = (v: string) => v.trim().replace(/\s+/g, ' ').toLowerCase();
    return norm(this.deleteTyped) === norm(this.nameLabel(row));
  }

  cancelDelete(): void {
    this.confirmingKey = null;
    this.deleteTyped = '';
    this.cdr.markForCheck();
  }

  /**
   * The Referrals Summary schedule is re-created by the server on every start,
   * so deleting it only holds until the next restart. Say so instead of letting
   * the row quietly come back.
   */
  isReseeded(row: ScheduleOverviewRow): boolean {
    return row.reportType === MANAGER_DASHBOARD_REPORT_TYPE_WIRE;
  }

  confirmDelete(row: ScheduleOverviewRow): void {
    const key = this.keyOf(row);
    if (this.busyKey || !this.deleteConfirmed(row)) return;
    this.busyKey = key;
    this.actionError = '';
    this.cdr.markForCheck();

    this.server.deleteWithToken(
      `report-notifications/reports/${encodeURIComponent(row.reportId)}/schedule?reportType=${encodeURIComponent(row.reportType)}`,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.rows = this.rows.filter(r => this.keyOf(r) !== key);
          if (this.expandedKey === key) this.expandedKey = null;
          this.confirmingKey = null;
          this.busyKey = null;
          this.cdr.markForCheck();
        },
        error: err => {
          this.actionError = this.refusalOr(err, `Could not delete "${this.nameLabel(row)}". Please try again.`);
          this.busyKey = null;
          this.cdr.markForCheck();
        },
      });
  }

  reload(): void {
    this.confirmingKey = null;
    this.load();
  }

  trackByKey = (_: number, row: ScheduleOverviewRow) => this.keyOf(row);
  trackBySetup = (_: number, entry: SetupEntry) => `${entry.reportType}::${entry.reportId}`;

  /** Monotonic: two loads can overlap (the reload on closing the editor and
   *  the one a late save triggers), and only the newest may paint - an older
   *  response landing last would show the list from before the write. */
  private loadToken = 0;

  private load(): void {
    const token = ++this.loadToken;
    this.loading = true;
    this.loadError = '';
    this.actionError = '';
    this.cdr.markForCheck();

    this.server.getWithToken<ScheduleOverviewRow[]>('report-notifications/schedules')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: rows => {
          if (token !== this.loadToken) return;
          this.rows = (rows || []).slice().sort((a, b) =>
            this.typeLabel(a).localeCompare(this.typeLabel(b)) ||
            this.nameLabel(a).localeCompare(this.nameLabel(b)));
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          if (token !== this.loadToken) return;
          this.loadError = 'Could not load the scheduled emails. Refresh the page to try again.';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }
}
